/**
 * Weather / visibility model, following ELITE XTS's concept: a surface
 * visibility distance plus an overcast cloud layer with a base altitude. Below
 * the cloud base the ground fades in through fog at the set visibility; above
 * the base the view whites out (in cloud). Presets mirror the ELITE manual.
 */
export interface WeatherState {
  visibilityMeters: number; // surface visibility (fog far distance)
  cloudBaseFt: number; // overcast base MSL; Infinity = no ceiling
  cloudTopFt: number; // top of the overcast layer
}

const SM = 1609.34;

export const VISIBILITY_PRESETS = [
  { name: 'Precision (1/2 SM)', visibilityMeters: 0.5 * SM, cloudBaseFt: 200 },
  { name: 'Non-precision (1 SM)', visibilityMeters: 1 * SM, cloudBaseFt: 600 },
  { name: 'CAVOK (30 SM)', visibilityMeters: 30 * SM, cloudBaseFt: Infinity },
];

export function makeWeather(presetIndex: number): WeatherState {
  const p = VISIBILITY_PRESETS[presetIndex] ?? VISIBILITY_PRESETS[1];
  return {
    visibilityMeters: p.visibilityMeters,
    cloudBaseFt: p.cloudBaseFt,
    cloudTopFt: p.cloudBaseFt === Infinity ? Infinity : p.cloudBaseFt + 1500,
  };
}

/**
 * How "in cloud" the aircraft is at a given altitude: 0 = clear, 1 = full IMC
 * whiteout. Used to drive the fog whiteout when climbing into the overcast.
 */
export function inCloudFactor(altitudeFt: number, wx: WeatherState): number {
  if (wx.cloudBaseFt === Infinity) return 0;
  if (altitudeFt < wx.cloudBaseFt) return 0;
  if (altitudeFt > wx.cloudTopFt) return 0.15; // on top: hazy but not white
  const t = (altitudeFt - wx.cloudBaseFt) / (wx.cloudTopFt - wx.cloudBaseFt);
  // Ramp quickly to whiteout just inside the base.
  return Math.min(1, t * 4);
}
